import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Download, Filter, Loader2, Settings2, Tags } from "lucide-react";
import toast from "react-hot-toast";
import Filters from "../Filters";
import { employeeService, statutoryReportsService } from "../../../services/api";
import { usePermissions } from "../../../rbac/PermissionsContext";
import { DsButton, DsPageHeader, DsSpinner } from "../../../design-system";
import StatutoryProfileModal from "./StatutoryProfileModal";
import StatutoryMaternityModal from "./StatutoryMaternityModal";
import StatutoryAliasModal from "./StatutoryAliasModal";
import { parsePayMonthValue } from "./statutoryPayMonthUtils";
import { displayActLabel } from "./statutoryStateUtils";

function periodControls(periodType) {
  switch (periodType) {
    case "pay_month":
      return ["payMonth"];
    case "pay_month_optional_employee":
      return ["payMonth", "employeeIdOptional"];
    case "pf_wage_year":
      return ["pfWageYear"];
    case "pf_wage_year_optional_employee":
      return ["pfWageYear", "employeeIdOptional"];
    case "calendar_year":
    case "calendar_year_optional_employee":
      return periodType === "calendar_year_optional_employee"
        ? ["year", "employeeIdOptional"]
        : ["year"];
    case "esi_half_year":
      return ["year", "half"];
    case "none":
      return [];
    default:
      return ["payMonth"];
  }
}

function validatePeriod(activeForm, values) {
  const pt = activeForm?.periodType;
  if ((pt === "pay_month" || pt === "pay_month_optional_employee") && !values.payMonth) {
    return "Select pay month";
  }
  if ((pt === "pf_wage_year" || pt === "pf_wage_year_optional_employee") && !values.pfWageYear) {
    return "Select PF wage year";
  }
  if ((pt === "calendar_year" || pt === "calendar_year_optional_employee") && !values.year) {
    return "Select calendar year";
  }
  if (pt === "esi_half_year" && (!values.year || !values.half)) return "Select ESI half-year";
  return null;
}

function validateSelection(stateCode, actCode, formCode, activeForm, values) {
  if (!stateCode) return "Select state";
  if (!actCode) return "Select act";
  if (!formCode || !activeForm) return "Select form";
  return validatePeriod(activeForm, values);
}

const SELECT_CLS =
  "h-9 w-full min-w-[140px] rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-2 text-xs text-[hsl(var(--ds-fg))]";

function Field({ label, children, className = "" }) {
  return (
    <div className={`flex min-w-[160px] flex-col gap-1 ${className}`}>
      <label className="text-[10px] font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
        {label}
      </label>
      {children}
    </div>
  );
}

export default function StatutoryReportsPage({ darkMode = false }) {
  const { hasScreenAccess, isAdmin } = usePermissions();
  const canView =
    isAdmin || hasScreenAccess("statutory_reports", "statutory_reports_hub", "view");
  const canExport =
    isAdmin || hasScreenAccess("statutory_reports", "statutory_reports_hub", "export");
  const canManageAliases =
    isAdmin || hasScreenAccess("statutory_reports", "statutory_form_aliases", "edit");

  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [catalog, setCatalog] = useState({ states: [], acts: [], forms: [], profile: null, aliases: [] });
  const [periodOptions, setPeriodOptions] = useState(null);
  const [stateCode, setStateCode] = useState("");
  const [actCode, setActCode] = useState("");
  const [formCode, setFormCode] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showAliasModal, setShowAliasModal] = useState(false);
  const [showMaternityModal, setShowMaternityModal] = useState(false);
  const [maternityDraft, setMaternityDraft] = useState({ employeeId: "", payMonth: "" });
  const [filterEmployeeIds, setFilterEmployeeIds] = useState([]);
  const [report, setReport] = useState(null);
  const [reportLoaded, setReportLoaded] = useState(false);
  const [employees, setEmployees] = useState([]);

  const [payMonth, setPayMonth] = useState("");
  const [pfWageYear, setPfWageYear] = useState("");
  const [year, setYear] = useState("");
  const [half, setHalf] = useState("");
  const [employeeId, setEmployeeId] = useState("");

  const [profileDraft, setProfileDraft] = useState({
    stateCode: "KA",
    establishmentTypes: ["shops_establishment", "factory"],
    shopsRegistrationNo: "",
    factoryLicenseNo: "",
    establishmentName: "",
    establishmentAddress: "",
    responsiblePersonEmployeeId: "",
    contractorName: "",
    contractorAddress: "",
    principalEmployerName: "",
    principalEmployerAddress: "",
    natureAndLocationOfWork: "",
    ownerName: "",
    linNumber: "",
    pfEstablishmentCode: "",
    esiEmployerCode: "",
    statutoryPfRate: "12",
  });

  const loadCatalog = useCallback(
    async (nextStateCode = stateCode) => {
      setLoading(true);
      try {
        const res = await statutoryReportsService.getCatalog(
          nextStateCode ? { stateCode: nextStateCode } : {}
        );
        const data = res?.data || res;
        setCatalog(data);
        if (data.periodOptions) {
          setPeriodOptions(data.periodOptions);
        }
        if (data.profile) {
          setProfileDraft({
            stateCode: data.profile.stateCode || nextStateCode,
            establishmentTypes: data.profile.establishmentTypes || [],
            shopsRegistrationNo: data.profile.shopsRegistrationNo || "",
            factoryLicenseNo: data.profile.factoryLicenseNo || "",
            establishmentName: data.profile.establishmentName || "",
            establishmentAddress: data.profile.establishmentAddress || "",
            responsiblePersonEmployeeId: data.profile.responsiblePersonEmployeeId || "",
            contractorName: data.profile.contractorName || "",
            contractorAddress: data.profile.contractorAddress || "",
            principalEmployerName: data.profile.principalEmployerName || "",
            principalEmployerAddress: data.profile.principalEmployerAddress || "",
            natureAndLocationOfWork: data.profile.natureAndLocationOfWork || "",
            ownerName: data.profile.ownerName || "",
            linNumber: data.profile.linNumber || "",
            pfEstablishmentCode: data.profile.pfEstablishmentCode || "",
            esiEmployerCode: data.profile.esiEmployerCode || "",
            statutoryPfRate: String(data.profile.statutoryPfRate ?? 12),
          });
        }
        const acts = (data.acts || []).filter((a) => !nextStateCode || a.stateCode === nextStateCode);
        if (nextStateCode && !acts.some((a) => a.actCode === actCode)) {
          setActCode("");
          setFormCode("");
        }
      } catch (err) {
        toast.error(err?.response?.data?.message || "Failed to load statutory catalog");
      } finally {
        setLoading(false);
      }
    },
    [stateCode]
  );

  const loadPeriodOptions = useCallback(async () => {
    try {
      const res = await statutoryReportsService.getPeriodOptions();
      setPeriodOptions(res?.data || res);
    } catch {
      setPeriodOptions(null);
    }
  }, []);

  const loadEmployees = useCallback(async () => {
    try {
      const res = await employeeService.getEmployees({ limit: 2000 });
      let list = [];
      if (Array.isArray(res)) list = res;
      else if (Array.isArray(res?.data)) list = res.data;
      else if (Array.isArray(res?.employees)) list = res.employees;
      setEmployees(list);
    } catch {
      setEmployees([]);
    }
  }, []);

  useEffect(() => {
    if (canView) {
      loadCatalog();
      loadPeriodOptions();
      loadEmployees();
    }
  }, [canView, loadCatalog, loadPeriodOptions, loadEmployees]);

  const actsForState = useMemo(
    () => (catalog.acts || []).filter((a) => a.stateCode === stateCode),
    [catalog.acts, stateCode]
  );

  const formsForAct = useMemo(
    () =>
      (catalog.forms || []).filter(
        (f) => f.actCode === actCode || (f.actCodes || []).includes(actCode)
      ),
    [catalog.forms, actCode]
  );

  const activeForm = useMemo(() => {
    if (!formCode) return null;
    return formsForAct.find((f) => f.formCode === formCode) || null;
  }, [formsForAct, formCode]);

  const periodValues = useMemo(
    () => ({ payMonth, pfWageYear, year, half, employeeId }),
    [payMonth, pfWageYear, year, half, employeeId]
  );

  const buildParams = useCallback(() => {
    const pt = activeForm?.periodType || "pay_month";
    const base = {
      formCode: activeForm?.formCode,
      employeeIds: filterEmployeeIds.length ? filterEmployeeIds.join(",") : undefined,
    };
    if (pt === "pay_month") return { ...base, payMonth };
    if (pt === "pay_month_optional_employee") {
      return { ...base, payMonth, employeeId: employeeId || undefined };
    }
    if (pt === "pf_wage_year") return { ...base, pfWageYear };
    if (pt === "pf_wage_year_optional_employee") {
      return { ...base, pfWageYear, employeeId: employeeId || undefined };
    }
    if (pt === "calendar_year") return { ...base, year };
    if (pt === "calendar_year_optional_employee") {
      return { ...base, year, employeeId: employeeId || undefined };
    }
    if (pt === "esi_half_year") return { ...base, year, half };
    if (pt === "none") return base;
    return { ...base, payMonth };
  }, [activeForm, payMonth, pfWageYear, year, half, employeeId, filterEmployeeIds]);

  const resetLoaded = () => {
    setReport(null);
    setReportLoaded(false);
  };

  const handleLoad = async () => {
    const err = validateSelection(stateCode, actCode, formCode, activeForm, periodValues);
    if (err) {
      toast.error(err);
      return;
    }
    setGenerating(true);
    resetLoaded();
    try {
      const res = await statutoryReportsService.generateReport(buildParams());
      const data = res?.data || res;
      setReport(data);
      setReportLoaded(true);
      const count = data?.meta?.rowCount ?? data?.rows?.length ?? 0;
      if (data?.meta?.noFinalizedMonths) {
        toast("No finalized payroll in this period — export may be empty until payroll is finalized", {
          icon: "ℹ️",
        });
      } else if (!count) {
        toast("Loaded — no rows for selected period / filters", { icon: "ℹ️" });
      } else {
        toast.success(`Loaded ${count} row(s). Export is now enabled.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load report");
      resetLoaded();
    } finally {
      setGenerating(false);
    }
  };

  const handleExportExcel = async () => {
    if (!reportLoaded || !report) {
      toast.error("Load the report first");
      return;
    }
    setExportingExcel(true);
    try {
      const response = await statutoryReportsService.exportExcel(buildParams());
      const blob = response?.data instanceof Blob ? response.data : new Blob([response.data]);
      const fileName =
        response?.headers?.["content-disposition"]?.match(/filename="(.+)"/)?.[1] ||
        `${activeForm?.formCode || "statutory"}.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        (e?.code === "ECONNABORTED" || /timeout/i.test(String(e?.message || ""))
          ? "Excel export timed out — server may have run out of memory. Retry after backend restart."
          : null) ||
        e?.message ||
        "Failed to export Excel";
      toast.error(msg);
    } finally {
      setExportingExcel(false);
    }
  };

  const handleExportPdf = async () => {
    if (!reportLoaded || !report) {
      toast.error("Load the report first");
      return;
    }
    setExportingPdf(true);
    try {
      const response = await statutoryReportsService.exportPdf(buildParams());
      const blob = response?.data instanceof Blob ? response.data : new Blob([response.data]);
      const fileName =
        response?.headers?.["content-disposition"]?.match(/filename="(.+)"/)?.[1] ||
        `${activeForm?.formCode || "statutory"}.pdf`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to export PDF");
    } finally {
      setExportingPdf(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!profileDraft.establishmentTypes?.length) {
      toast.error("Select at least one establishment type");
      return;
    }
    try {
      await statutoryReportsService.updateProfile({
        ...profileDraft,
        responsiblePersonEmployeeId: profileDraft.responsiblePersonEmployeeId
          ? Number(profileDraft.responsiblePersonEmployeeId)
          : null,
        statutoryPfRate: profileDraft.statutoryPfRate ? Number(profileDraft.statutoryPfRate) : 12,
      });
      toast.success("Establishment profile saved");
      setShowProfileModal(false);
      loadCatalog(stateCode);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save profile");
    }
  };

  const handleSaveMaternity = async () => {
    const normalizedPayMonth = parsePayMonthValue(maternityDraft.payMonth);
    if (!maternityDraft.employeeId || !normalizedPayMonth) {
      toast.error("Employee and pay month are required");
      return;
    }
    try {
      await statutoryReportsService.upsertMaternity({
        ...maternityDraft,
        employeeId: Number(maternityDraft.employeeId),
        payMonth: normalizedPayMonth,
      });
      setPayMonth(normalizedPayMonth);
      toast.success("Maternity details saved");
      setShowMaternityModal(false);
      if (activeForm?.formCode === "FORM_A_MATERNITY" && formCode && stateCode && actCode) {
        handleLoad();
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save maternity details");
    }
  };

  const handleSaveAlias = async (payload) => {
    if (!canManageAliases) {
      toast.error("You do not have permission to manage form names");
      return;
    }
    try {
      await statutoryReportsService.upsertAlias(payload);
      toast.success("Form name saved");
      loadCatalog(stateCode);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save form name");
    }
  };

  const handleDeleteAlias = async (id) => {
    try {
      await statutoryReportsService.deleteAlias(id);
      toast.success("Removed");
      loadCatalog(stateCode);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to remove");
    }
  };

  const border = darkMode ? "border-[hsl(var(--ds-border))]" : "border-gray-200";
  const controls = activeForm ? periodControls(activeForm.periodType) : [];
  const exportEnabled = reportLoaded && canExport;

  if (!canView) {
    return (
      <div className="ds-page p-6">
        <p className="text-sm text-[hsl(var(--ds-muted-fg))]">
          You do not have access to Statutory Reports.
        </p>
      </div>
    );
  }

  return (
    <div className="ds-page flex min-h-0 flex-1 flex-col">
      <DsPageHeader
        title="Statutory Reports"
        actions={
          <div className="flex flex-wrap gap-2">
            <DsButton variant="outline" size="sm" onClick={() => setShowProfileModal(true)}>
              <Settings2 size={14} className="mr-1" /> Establishment profile
            </DsButton>
            {canManageAliases && (
              <DsButton variant="outline" size="sm" onClick={() => setShowAliasModal(true)}>
                <Tags size={14} className="mr-1" /> Manage form names
              </DsButton>
            )}
          </div>
        }
      />

      <div className="ds-page-content flex min-h-0 flex-1 flex-col gap-4 p-4 md:p-6">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <DsSpinner />
          </div>
        ) : (
          <>
            {showFilters && (
              <Filters
                darkMode={darkMode}
                onApply={(f) => {
                  setFilterEmployeeIds(f?.selectedEmployeeIds || []);
                  setShowFilters(false);
                  resetLoaded();
                  toast.success("Filters applied");
                }}
                onClose={() => setShowFilters(false)}
                onClear={() => {
                  setFilterEmployeeIds([]);
                  resetLoaded();
                }}
                initialFilters={{ dateFilterType: "all" }}
              />
            )}

            <section className={`rounded-lg border bg-[hsl(var(--ds-surface))] p-4 ${border}`}>
              <div className="flex flex-wrap items-end gap-3">
                <Field label="State">
                  <select
                    className={SELECT_CLS}
                    value={stateCode}
                    onChange={(e) => {
                      setStateCode(e.target.value);
                      setActCode("");
                      setFormCode("");
                      resetLoaded();
                      loadCatalog(e.target.value);
                    }}
                  >
                    <option value="">-- Select State --</option>
                    {(catalog.states || []).map((s) => (
                      <option key={s.stateCode} value={s.stateCode}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Act" className="min-w-[200px] flex-1">
                  <select
                    className={SELECT_CLS}
                    value={actCode}
                    onChange={(e) => {
                      setActCode(e.target.value);
                      setFormCode("");
                      resetLoaded();
                    }}
                    disabled={!stateCode}
                  >
                    <option value="">-- Select Act --</option>
                    {actsForState.map((a) => (
                      <option key={a.actCode} value={a.actCode}>
                        {displayActLabel(a.label)}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Form" className="min-w-[200px] flex-1">
                  <select
                    className={SELECT_CLS}
                    value={formCode}
                    disabled={!actCode}
                    onChange={(e) => {
                      setFormCode(e.target.value);
                      resetLoaded();
                    }}
                  >
                    <option value="">-- Select Form --</option>
                    {formsForAct.map((f) => (
                      <option key={f.formCode} value={f.formCode}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                </Field>

                {controls.includes("payMonth") && (
                  <Field label="Pay month">
                    <select className={SELECT_CLS} value={payMonth} onChange={(e) => { setPayMonth(e.target.value); resetLoaded(); }}>
                      <option value="">-- Select month --</option>
                      {(periodOptions?.payMonths || []).map((m) => (
                        <option key={m.value} value={m.value}>
                          {m.label}{m.hasData ? "" : " (no payroll)"}
                        </option>
                      ))}
                    </select>
                  </Field>
                )}

                {controls.includes("pfWageYear") && (
                  <Field label="PF wage year (Mar)">
                    <select className={SELECT_CLS} value={pfWageYear} onChange={(e) => { setPfWageYear(e.target.value); resetLoaded(); }}>
                      <option value="">-- Select PF wage year --</option>
                      {(periodOptions?.pfWageYears || []).map((y) => (
                        <option key={y.value} value={y.value}>
                          {y.label}{y.hasData ? "" : " · pending payroll"}
                        </option>
                      ))}
                    </select>
                  </Field>
                )}

                {controls.includes("year") && activeForm?.periodType !== "esi_half_year" && (
                  <Field label="Calendar year">
                    <select className={SELECT_CLS} value={year} onChange={(e) => { setYear(e.target.value); resetLoaded(); }}>
                      <option value="">-- Select year --</option>
                      {(periodOptions?.calendarYears || []).map((y) => (
                        <option key={y.value} value={y.value}>
                          {y.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                )}

                {controls.includes("half") && (
                  <>
                    <Field label="ESI year (Apr)">
                      <select className={SELECT_CLS} value={year} onChange={(e) => { setYear(e.target.value); resetLoaded(); }}>
                        <option value="">-- Select ESI year --</option>
                        {[...new Set((periodOptions?.esiHalfYears || []).map((h) => h.value))].map((y) => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Half year">
                      <select className={SELECT_CLS} value={half} onChange={(e) => { setHalf(e.target.value); resetLoaded(); }}>
                        <option value="">-- Select half --</option>
                        <option value="1">H1 — Apr to Sep</option>
                        <option value="2">H2 — Oct to Mar</option>
                      </select>
                    </Field>
                  </>
                )}

                {controls.includes("employeeIdOptional") && (
                  <Field label="Employee (optional)" className="min-w-[200px] flex-1">
                    <select className={SELECT_CLS} value={employeeId} onChange={(e) => { setEmployeeId(e.target.value); resetLoaded(); }}>
                      <option value="">All employees</option>
                      {employees.map((emp) => (
                        <option key={emp.id} value={emp.id}>
                          {emp.empCode} — {[emp.user?.name, emp.user?.lastName].filter(Boolean).join(" ")}
                        </option>
                      ))}
                    </select>
                  </Field>
                )}

                <div className="flex flex-wrap items-center gap-2 pb-0.5">
                  <button
                    type="button"
                    onClick={() => setShowFilters(true)}
                    className={`inline-flex h-9 items-center gap-1 rounded-md border px-3 text-xs ${border}`}
                  >
                    <Filter size={14} /> Filters
                    {filterEmployeeIds.length > 0 && (
                      <span className="rounded bg-[hsl(var(--ds-primary))] px-1.5 text-white">
                        {filterEmployeeIds.length}
                      </span>
                    )}
                  </button>

                  <DsButton
                    onClick={handleLoad}
                    disabled={generating || !stateCode || !actCode || !formCode || !activeForm}
                  >
                    {generating ? <Loader2 size={14} className="animate-spin" /> : "Load"}
                  </DsButton>

                  {activeForm?.formCode === "FORM_A_MATERNITY" && (
                    <DsButton
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setMaternityDraft((p) => ({
                          ...p,
                          payMonth: parsePayMonthValue(payMonth) || "",
                        }));
                        setShowMaternityModal(true);
                      }}
                    >
                      Maternity details
                    </DsButton>
                  )}

                  <DsButton
                    variant="outline"
                    size="sm"
                    disabled={!exportEnabled || exportingExcel}
                    onClick={handleExportExcel}
                  >
                    {exportingExcel ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} className="mr-1" />}
                    Excel
                  </DsButton>

                  <DsButton
                    variant="outline"
                    size="sm"
                    disabled={!exportEnabled || exportingPdf}
                    onClick={handleExportPdf}
                  >
                    {exportingPdf ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} className="mr-1" />} PDF
                  </DsButton>
                </div>
              </div>

              {reportLoaded && report && (
                <p className="mt-3 text-xs text-[hsl(var(--ds-muted-fg))]">
                  <strong>{report.title}</strong> — {report.period?.label} — {report.meta?.rowCount ?? 0} row(s)
                  {report.meta?.noFinalizedMonths ? " · No finalized payroll months in period" : ""}
                </p>
              )}
            </section>
          </>
        )}
      </div>

      <StatutoryProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        profileDraft={profileDraft}
        setProfileDraft={setProfileDraft}
        employees={employees}
        onSave={handleSaveProfile}
      />

      <StatutoryMaternityModal
        isOpen={showMaternityModal}
        onClose={() => setShowMaternityModal(false)}
        darkMode={darkMode}
        maternityDraft={maternityDraft}
        setMaternityDraft={setMaternityDraft}
        employees={employees}
        onSave={handleSaveMaternity}
      />

      <StatutoryAliasModal
        isOpen={showAliasModal}
        onClose={() => setShowAliasModal(false)}
        catalog={catalog}
        branchStates={catalog.branchStates || periodOptions?.branchStates || []}
        aliases={catalog.aliases || []}
        onSave={handleSaveAlias}
        onDelete={handleDeleteAlias}
      />
    </div>
  );
}
